import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import SelectCountry from "../../components/SelectCountry";
import SelectMapAddress from "../../components/SelectMapAddress";
import { AddressComponent } from "../../types/common";
import { useGetCountriesQuery } from "../../services/country.service";
import TitleDivider from "../../components/TitleDivider";
import PinDropIcon from "@mui/icons-material/PinDrop";
import SearchIcon from "@mui/icons-material/Search";
import CoordinateInfo from "../../components/CoordinateInfo";
import {
  useAddCustomerMutation,
  useEditCustomerMutation,
} from "../../services/customer.service";
import { Customer } from "../../types/customer";
import { ConstructionOutlined } from "@mui/icons-material";
import { useUILabel } from "../../hooks/useUILabel";

interface ClientModalFormProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: number) => void;
  data: Customer | null;
}

const ClientModalForm = ({
  open,
  onClose,
  onCreated,
  data,
}: ClientModalFormProps) => {
  const [add, { isLoading: adding }] = useAddCustomerMutation();
  const [edit, { isLoading: editing }] = useEditCustomerMutation();
  const { data: countries } = useGetCountriesQuery();
  const [showAddress, setShowAddress] = React.useState(false);
  const createClientLabel = useUILabel("client.createClient");
  const editClientLabel = useUILabel("client.editClient");
  const clientNameLabel = useUILabel("client.clientName");
  const phoneNumberLabel = useUILabel("common.phoneNumber");
  const emailLabel = useUILabel("common.email");
  const addressLabel = useUILabel("common.address");
  const createLabel = useUILabel("common.create");
  const updateLabel = useUILabel("common.update");
  const cancelLabel = useUILabel("common.cancel");
  const cityLabel = useUILabel("common.city");
  const stateLabel = useUILabel("common.state");
  const zipLabel = useUILabel("common.zip");
  const address1Label = useUILabel("client.address1");
  const address2Label = useUILabel("client.address2");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");
  const invalidLabel = useUILabel("common.invalid");
  const enteredValueDoesNotMatchEmailFormatLabel = useUILabel("common.enteredValueDoesNotMatchEmailFormat");
  const searchNewAddressLabel = useUILabel("client.searchNewAddress");

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    setError,
    formState: { errors },
    watch,
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!payload.country) {
        setError("country", { type: "custom", message: invalidLabel });
        return;
      }
      if (!data) {
        const cust = await add(payload).unwrap();
        if (onCreated) {
          onCreated(cust.id);
        }
      } else {
        if (!payload.password) delete payload.password;
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (!open) return;

    setValue("first_name", data?.first_name);
    setValue("phone_number", data?.phone_number || "");
    setValue("email", data?.email || "");
    setValue("physical_address_1", data?.physical_address_1);
    setValue("physical_address_2", data?.physical_address_2);
    setValue("city", data?.city);
    setValue("state", data?.state);
    setValue("zip", data?.zip);
    setValue("country", data?.country_id || null);
    const [lng, lat] = data?.coordinate?.coordinates || [];
    setValue("lat", lat || 0);
    setValue("lng", lng || 0);
    setShowAddress(!!lat && !!lng);

    clearErrors();
  }, [data, open]);

  const handleMapAddressChange = (
    address: AddressComponent,
    lat?: number,
    lng?: number
  ) => {
    setValue(
      "physical_address_1",
      `${address.street} ${address.street_number}`
    );
    setValue("city", address.city);
    setValue("state", address.state);
    setValue("zip", address.postal_code);
    setValue("lat", lat);
    setValue("lng", lng);
    const found = countries?.find(
      (it) => it.code.toLowerCase() == address.country.toLowerCase()
    );
    setValue("country", found?.id);
    setShowAddress(true);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth={true} 
      maxWidth="sm"
      PaperProps={{
        sx: {
          "@media (orientation: landscape) and (max-width: 900px)": {
            maxHeight: "90vh",
            margin: (theme) => theme.spacing(0.5, 1),
            width: "calc(100% - 16px)",
            maxWidth: "calc(100% - 16px) !important",
          },
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle 
          id="alert-dialog-title"
          sx={{
            "@media (orientation: landscape) and (max-width: 900px)": {
              padding: (theme) => theme.spacing(1, 1.5),
              fontSize: "0.9375rem",
            },
          }}
        >
          {!data ? createClientLabel : editClientLabel}
        </DialogTitle>
        <DialogContent
          sx={{
            "@media (orientation: landscape) and (max-width: 900px)": {
              padding: (theme) => theme.spacing(1.5, 2),
              maxHeight: "calc(90vh - 140px)",
              overflowY: "auto",
            },
          }}
        >
          <Controller
            name="first_name"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <TextField
                label={clientNameLabel}
                InputLabelProps={{ shrink: !!value }}
                value={value}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.first_name}
                helperText={!!errors.first_name && thisFieldIsRequiredLabel}
              />
            )}
          />
          <Stack direction={"row"} spacing={2} sx={{ mt: 2 }}>
            <Controller
              name="phone_number"
              control={control}
              rules={{ required: true, minLength: 6 }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <TextField
                  label={phoneNumberLabel}
                  value={value}
                  fullWidth
                  sx={{ mt: 0 }}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="number"
                  error={!!errors.phone_number}
                  helperText={!!errors.phone_number && thisFieldIsRequiredLabel}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: enteredValueDoesNotMatchEmailFormatLabel,
                },
              }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <TextField
                  label={emailLabel}
                  value={value}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  type="email"
                  error={!!errors.email}
                  helperText={!!errors.email && thisFieldIsRequiredLabel}
                />
              )}
            />
          </Stack>
          {!showAddress && (
            <SelectMapAddress
              onChange={handleMapAddressChange}
              error={!!errors.country}
              helperText={!!errors.country && thisFieldIsRequiredLabel}
            />
          )}
          {showAddress && (
            <Box>
              <TitleDivider title={addressLabel} />
              <Button
                onClick={() => {
                  setValue("country", null); //reset
                  setShowAddress(false);
                }}
                size="small"
                startIcon={<SearchIcon />}
              >
                {searchNewAddressLabel}
              </Button>
              <Controller
                name="physical_address_1"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <TextField
                    label={address1Label}
                    InputLabelProps={{ shrink: !!value }}
                    value={value}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.physical_address_1}
                    helperText={
                      !!errors.physical_address_1 && thisFieldIsRequiredLabel
                    }
                  />
                )}
              />
              <Controller
                name="physical_address_2"
                control={control}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                    <TextField
                      label={address2Label}
                    InputLabelProps={{ shrink: !!value }}
                    value={value}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    sx={{ mt: 2 }}
                  />
                )}
              />
              <Controller
                name="country"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <SelectCountry
                    onChange={onChange}
                    value={value}
                    error={!!errors.country}
                    helperText={!!errors.country && thisFieldIsRequiredLabel}
                  />
                )}
              />
              <Stack direction={"row"} spacing={2} sx={{ mt: 2 }}>
                <Controller
                  name="city"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <TextField
                      label={cityLabel}
                      InputLabelProps={{ shrink: !!value }}
                      value={value}
                      fullWidth
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.city}
                      helperText={!!errors.city && thisFieldIsRequiredLabel}
                    />
                  )}
                />
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <TextField
                      label={stateLabel}
                      InputLabelProps={{ shrink: !!value }}
                      value={value}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.state}
                      helperText={!!errors.state && thisFieldIsRequiredLabel}
                    />
                  )}
                />
                <Controller
                  name="zip"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <TextField
                      label={zipLabel}
                      InputLabelProps={{ shrink: !!value }}
                      value={value}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.zip}
                      helperText={!!errors.zip && thisFieldIsRequiredLabel}
                    />
                  )}
                />
              </Stack>
              <CoordinateInfo lat={watch("lat")} lng={watch("lng")} />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            "@media (orientation: landscape) and (max-width: 900px)": {
              padding: (theme) => theme.spacing(1, 1.5),
              gap: (theme) => theme.spacing(0.75),
              "& button": {
                fontSize: "0.875rem",
                padding: (theme) => theme.spacing(0.5, 1.5),
                minHeight: "36px",
              },
            },
          }}
        >
          <Button onClick={onClose}>{cancelLabel}</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
          >
            {!data ? createLabel : updateLabel}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default ClientModalForm;
